import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { supabase, configured } from '../lib/supabase';
import { APP_NAME, APP_VERSION } from '../lib/version';
import { Button, Field } from '../ui/kit';

export function Brand() {
  return (
    <div className="brand">
      <span className="logo">C3</span>
      <span>
        <strong>{APP_NAME}</strong>
        <small>Skyward Industries · construction control</small>
      </span>
    </div>
  );
}

type Mode = 'password' | 'code-ask' | 'code-enter';
type CodeKind = 'email' | 'invite' | 'recovery';

// Sign-in with a password, or with a 6-digit code from email.
// Codes (not links) because email scanners such as Microsoft Safe Links open links and use them up.
// Invite emails send people to /welcome?email=… → this page in "enter your code" mode.
export default function Login({ welcome = false }: { welcome?: boolean }) {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<Mode>(welcome ? 'code-enter' : 'password');
  const [kind, setKind] = useState<CodeKind>(welcome ? 'invite' : 'email');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(
    welcome ? { tone: 'ok', text: 'Enter the 6-digit code from your invitation email.' } : null,
  );

  async function signIn(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setMsg({ tone: 'err', text: error.message === 'Invalid login credentials' ? 'Email or password is wrong' : error.message });
  }

  async function sendCode(k: 'email' | 'recovery') {
    if (!email.trim()) return setMsg({ tone: 'err', text: 'Type your email address first' });
    setBusy(true);
    setMsg(null);
    const { error } =
      k === 'email'
        ? await supabase.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: false } })
        : await supabase.auth.resetPasswordForEmail(email.trim());
    setBusy(false);
    if (error) return setMsg({ tone: 'err', text: error.message });
    setKind(k);
    setMode('code-enter');
    setMsg({ tone: 'ok', text: 'We emailed you a 6-digit code. It works for one hour.' });
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    const token = code.replace(/\s+/g, '');
    if (!/^\d{6,10}$/.test(token)) return setMsg({ tone: 'err', text: 'Enter the code from the email (numbers only)' });
    setBusy(true);
    setMsg(null);
    const { error } = await supabase.auth.verifyOtp({ email: email.trim(), token, type: kind });
    setBusy(false);
    if (error) return setMsg({ tone: 'err', text: error.message.includes('expired') ? 'That code has expired or was already used — ask for a new one.' : error.message });
    if (kind === 'invite' || kind === 'recovery') nav('/set-password', { replace: true });
    else nav('/', { replace: true });
  }

  const note = msg ? (
    <div className={msg.tone === 'err' ? 'error-box' : 'banner banner-info'}>{msg.text}</div>
  ) : null;

  return (
    <div className="auth">
      {mode === 'password' ? (
        <form className="auth-card" onSubmit={signIn}>
          <Brand />
          <h1 style={{ fontSize: 22 }}>Sign in</h1>
          {!configured ? <div className="error-box">This site is missing its database settings.</div> : null}
          <Field label="Email">
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Password">
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          {note}
          <Button kind="primary" type="submit" disabled={busy}>
            {busy ? 'Please wait…' : 'Sign in'}
          </Button>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <Button kind="ghost" onClick={() => void sendCode('email')} disabled={busy}>
              Email me a sign-in code
            </Button>
            <Button kind="ghost" onClick={() => void sendCode('recovery')} disabled={busy}>
              Forgot password
            </Button>
          </div>
          <Button
            kind="ghost"
            onClick={() => {
              setKind('invite');
              setMode('code-enter');
              setMsg({ tone: 'ok', text: 'Enter your email and the 6-digit code from your invitation.' });
            }}
          >
            I have an invitation code
          </Button>
          <p className="muted small" style={{ margin: 0 }}>
            Access is by invitation only. {APP_NAME} V{APP_VERSION}
          </p>
        </form>
      ) : (
        <form className="auth-card" onSubmit={verify}>
          <Brand />
          <h1 style={{ fontSize: 22 }}>{kind === 'invite' ? 'Accept your invitation' : kind === 'recovery' ? 'Reset your password' : 'Sign in with a code'}</h1>
          <Field label="Email">
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="6-digit code">
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              style={{ fontSize: 22, letterSpacing: 4 }}
              required
            />
          </Field>
          {note}
          <Button kind="primary" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <Button kind="ghost" onClick={() => void sendCode(kind === 'recovery' ? 'recovery' : 'email')} disabled={busy || kind === 'invite'}>
              Send a new code
            </Button>
            <Button kind="ghost" onClick={() => { setMode('password'); setMsg(null); }}>
              Back to password sign-in
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
